import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Card, Accordion, Row, Col } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext, get } from "react-hook-form";
import { AxiosResponse } from "axios";

import errorToast from "helpers/errorToast";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { getScheduleOption, getCompanyContactBook } from "api";
import { Contact } from "api/models";
import Alert from "components/molecules/Alert/Alert";
import usePermissions from "hooks/usePermissions";
import { FormStepTypes, FormStepValidationTypes } from "constants/";
import testIds from "constants/testIds";
import Badge from "components/v2/Badge/Badge";
import { StopType } from "types/MultiStop";
import PickupDelivery from "./PickupDelivery";
import { IScheduleOptions } from "./Types";
import useGetMultiDrop from "./hooks/useGetMultiDrop";
import FormHr from "../Common/FormHr";
import MultiDropForm from "./MultiDropForm/MultiDropForm";
import NotifyPartyAndConsignee from "./NotifyPartyAndConsignee/NotifyPartyAndConsignee";
import useGetNotifyPartyAndConsignee from "./hooks/useGetNotifyPartyAndConsignee";
import ContactForm from "./NotifyPartyAndConsignee/ContactForm";
import MultiStopForm from "./MultiStopForm/MultiStopForm";
import useGetMultiStop from "./hooks/useGetMultiStop";

function ScheduleForm(props: any) {
  const {
    formState,
    isQuoteRequest,
    isOffPlatformShipment,
    setOffPlatformOptionalFields,
    isMiniTender = false,
    validateSection = undefined,
    shouldValidateSection = undefined,
    isNewShipment,
    childRef,
  } = props;

  const [contactBook, setContactBook] = useState<Contact[]>([]);
  const [scheduleOptions, setScheduleOptions] = useState<IScheduleOptions[]>([]);

  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const { getPaymentLocationLabel } = usePermissions();
  const {
    getValues,
    setValue,
    formState: { errors },
    setError,
  } = useFormContext();

  const { isMultiStopQuoteRequest } = useGetMultiStop();
  const { isMultiDropQuoteRequest } = useGetMultiDrop();
  const { isNotifyPartyAndConsignee } = useGetNotifyPartyAndConsignee();
  const [isNotifyPartyRequired, setIsNotifyPartyRequired] = useState(false);
  const previousMultiDropQuoteRequest = useRef<boolean | undefined>(undefined);

  const currentActiveStep = searchParams.get("active_step");
  const scheduleFields = FormStepValidationTypes[FormStepTypes.SCHEDULE] as string[];
  const hasError = scheduleFields.some((item) => get(errors, item));
  const location = getValues("organizational.location") || getValues("shipment.location");

  // WEB-11639: leaving Multi-Drop must clear packing-list stop keys and collapse
  // leftover stops so Pickup/Delivery packing-list fields cannot stay stale.
  useEffect(() => {
    const wasMultiDrop = previousMultiDropQuoteRequest.current;
    previousMultiDropQuoteRequest.current = isMultiDropQuoteRequest;
    if (!(wasMultiDrop === true && isMultiDropQuoteRequest === false)) return;

    const stops = getValues("schedule.stops") || [];
    if (stops.length > 2) {
      const firstPickup = stops.find((stop: any) => stop.stop_type === StopType.PICKUP) || stops[0];
      const lastDelivery =
        [...stops].reverse().find((stop: any) => stop.stop_type === StopType.DROPOFF) || stops[stops.length - 1];
      setValue("schedule.stops", [firstPickup, lastDelivery].filter(Boolean), { shouldDirty: true });
    }
    const packingList = getValues("packing_list") || [];
    packingList.forEach((_: unknown, index: number) => {
      setValue(`packing_list.${index}.pickup_stop_key`, null, { shouldDirty: true });
      setValue(`packing_list.${index}.delivery_stop_key`, null, { shouldDirty: true });
    });
  }, [isMultiDropQuoteRequest, getValues, setValue]);

  useEffect(() => {
    getContactBook();
  }, [getValues("organizational.location"), getValues("shipment.location")]);

  const getContactBook = () => {
    if (!location) return;

    getCompanyContactBook({
      params: {
        location,
      },
    })
      .then((res: AxiosResponse) => setContactBook(res.data.results))
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "new-request.schedule.couldNotRetrieveContactBook",
            defaultMessage: "Could not retrieve contact book. Please try again later.",
          }),
        ),
      );
    getScheduleOption(location)
      .then((res: AxiosResponse) => {
        setScheduleOptions(res.data.schedule_options);
        if (isOffPlatformShipment) {
          setOffPlatformOptionalFields(res.data.schedule_options.off_platform_shipment_optional_details);
        }
        if (shouldValidateSection && validateSection && shouldValidateSection.current) {
          setTimeout(() => {
            validateSection(FormStepTypes.SCHEDULE);
          }, 200);
        }
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveScheduleSetting",
            defaultMessage: "Could not retrieve schedule settings. Please try again later.",
          }),
        ),
      );
  };

  useImperativeHandle(childRef, () => ({ checkStepValidation }));

  const checkStepValidation = () => {
    if (isMultiStopQuoteRequest) {
      const stops = getValues("schedule.stops");
      if (stops.some((stop: any) => stop.stop_type === StopType.DROPOFF)) return true;
      setError("schedule.multistop.types", {
        type: "manual",
        message: intl.formatMessage({
          id: "new-request.schedule.multistop.error.atLeastOneDelivery",
          defaultMessage: "At least one delivery is needed to complete the request",
        }),
      });
      return false;
    }
    return true;
  };

  return (
    <Card className="mb-3 form-container" data-testid={testIds.REQUEST_FORM_SCHEDULE_SECTION}>
      <Accordion.Toggle as={Card.Header} className="accordion-header" eventKey={FormStepTypes.SCHEDULE}>
        <div className="d-flex align-items-center">
          <div>
            {!isOffPlatformShipment ? (
              <>
                {(hasError || formState.draftSaved[FormStepTypes.SCHEDULE]) && (
                  <div>{hasError ? <ExclamationIcon className="mr-2" /> : <CheckIcon className="mr-2" />}</div>
                )}
              </>
            ) : (
              <div>
                {hasError ? (
                  <ExclamationIcon className="mr-2" />
                ) : currentActiveStep !== "schedule" &&
                  getValues("schedule.pickup_address_name") &&
                  getValues("schedule.stop1_address_name") ? (
                  <CheckIcon className="mr-2" />
                ) : null}
              </div>
            )}
          </div>
          <h5 className="mr-md">
            <FormattedMessage id="general.schedule" defaultMessage="Schedule" />
          </h5>
          {isMultiStopQuoteRequest && getValues("schedule.stops")?.length > 2 && (
            <Badge
              type="category-badge"
              color="purple"
              size="md"
              label={
                <div>
                  <FormattedMessage id="new-request.schedule.multistop" defaultMessage="Multi-stop" />:
                  <span className="font-regular">
                    {" "}
                    {getValues("schedule.stops")?.length} <FormattedMessage id="general.stops" defaultMessage="Stops" />
                  </span>
                </div>
              }
            />
          )}
        </div>

        <SortIcon
          fill="#000"
          width={8}
          height={7}
          transform={`rotate(${currentActiveStep === FormStepTypes.SCHEDULE ? 180 : 0})`}
        />
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={FormStepTypes.SCHEDULE}>
        {!getValues("shipment.location") && !getValues("organizational.location") ? (
          <Row>
            <Col>
              <Alert variant="warning" className="mb-3 ml-3">
                <FormattedMessage
                  id="new-request.organisational-details.selectPaymentLocationPrompt"
                  defaultMessage="Please select a {paymentLocation} before continuing"
                  values={{ paymentLocation: getPaymentLocationLabel() }}
                />
              </Alert>
            </Col>
          </Row>
        ) : (
          <Card.Body className="pt-none pb-none">
            {errors?.schedule?.multistop && (
              <Alert className="w-100" variant="danger">
                <FormattedMessage
                  id="new-request.schedule.multistop.error.atLeastOneDelivery"
                  defaultMessage="At least one delivery is needed to complete the request"
                />
              </Alert>
            )}
            {isMultiStopQuoteRequest ? (
              <div data-testid={testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_SECTION}>
                <MultiStopForm contactBook={contactBook} formState={formState} />
              </div>
            ) : isMultiDropQuoteRequest ? (
              <MultiDropForm contactBook={contactBook} />
            ) : (
              <>
                <div data-testid={testIds.REQUEST_FORM_SCHEDULE_PICKUP_BLOCK}>
                  <PickupDelivery
                    isOffPlatformShipment={isOffPlatformShipment}
                    isPickupOrDelivery="pickup"
                    isQuoteRequest={isQuoteRequest}
                    contactBook={contactBook}
                    scheduleOptions={scheduleOptions}
                    isNewShipment={isNewShipment}
                    isMiniTender={isMiniTender}
                    formState={formState}
                  />
                </div>
                <FormHr />
                <div data-testid={testIds.REQUEST_FORM_SCHEDULE_DELIVERY_BLOCK}>
                  <PickupDelivery
                    isOffPlatformShipment={isOffPlatformShipment}
                    isPickupOrDelivery="stop1"
                    isQuoteRequest={isQuoteRequest}
                    contactBook={contactBook}
                    scheduleOptions={scheduleOptions}
                    isNewShipment={isNewShipment}
                    isMiniTender={isMiniTender}
                    formState={formState}
                  />
                </div>
              </>
            )}

            {isNotifyPartyAndConsignee && (
              <>
                <FormHr />
                <div data-testid={testIds.REQUEST_FORM_SCHEDULE_CONSIGNEE_SECTION}>
                  <NotifyPartyAndConsignee partyType="consignee" />
                </div>
                <div className="ml-xl pb-3" style={{ paddingLeft: "200px" }}>
                  <ContactForm
                    partyType="consignee"
                    contactBook={contactBook}
                    disabled={getValues("schedule.consignee.same_as_delivery_address_contact")}
                    required
                  />
                </div>
                <FormHr />
                <div data-testid={testIds.REQUEST_FORM_SCHEDULE_NOTIFY_PARTY_SECTION}>
                  <NotifyPartyAndConsignee partyType="notify_party" setIsNotifyPartyRequired={setIsNotifyPartyRequired} />
                </div>
                <div className="ml-xl pb-5" style={{ paddingLeft: "200px" }}>
                  <ContactForm
                    partyType="notify_party"
                    contactBook={contactBook}
                    disabled={["sameAsConsigneeAddressContact", "sameAsDeliveryAddressContact"].includes(
                      getValues("schedule.notify_party.same_as_address_contact"),
                    )}
                    required={isNotifyPartyRequired}
                  />
                </div>
              </>
            )}
          </Card.Body>
        )}
      </Accordion.Collapse>
    </Card>
  );
}

export default ScheduleForm;
